import { Component, ChangeDetectionStrategy, OnInit, Input, Output, EventEmitter, Inject } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";

import { ContextService } from "@core/service/context.service";
import { BehaviorSubject, of, EMPTY } from "rxjs";
import { tap, filter, catchError } from "rxjs/operators";
import { IContextCompany, IContextProperty, IContextSelected, IContextInStorage } from "@schema/context";
import { PermissionService } from "@core/service/permission.service";
import { Router } from "@angular/router";
import { SESSION_STORAGE, StorageService } from "ngx-webstorage-service";
import { PageTitleService } from "@core/service/page-title.service";

@Component({
  selector: "app-context-selector",
  templateUrl: "./context-selector.component.html",
  styleUrls: ["./context-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContextSelectorComponent implements OnInit {

  /**
   * If the user is switching contexts, or is new and selecting thier initial
   */
  @Input()
  public changingContext = false;

  /**
   * Reactive form
   */
  public form: FormGroup;

  /**
   * If we're loading the context or not
   */
  public loading$: BehaviorSubject<string> = new BehaviorSubject(null);

  /**
   * If there was an loading the context or not
   */
  public error$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * The company / properties available
   */
  public contextsAvailable: IContextCompany[] = [];

  /**
   * Return the available properties for the selected company when applicable
   */
  get properties(): IContextProperty[] {
    const id = this.form.get("company").value;
    if (id) {
      const selectedCompany = this.contextsAvailable.find((company: IContextCompany) => {
        return company.id === id;
      });
      if (selectedCompany) {
        return selectedCompany.properties;
      }
    }
    return null;
  }

  /**
   * The initial context, if any
   */
  @Input()
  public initialContext: IContextInStorage;

  /**
   * Event when the context was selected
   */
  @Output()
  public contextUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event when the user clicked the cancel button
   */
  @Output()
  public cancelled: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    protected readonly _contextService: ContextService,
    protected readonly _fb: FormBuilder,
    protected readonly _permissionService: PermissionService,
    protected readonly _router: Router,
    protected readonly _pageTitle: PageTitleService,
    @Inject(SESSION_STORAGE) protected _storage: StorageService,
  ) { }

  ngOnInit(): void {
    this.loading$.next("companies");

    this._updateTitle("Loading");

    this._contextService.loadContexts({ retryMaxAttempts: 2 })
      .pipe(
        tap(() => {
          this._finishLoading();
        }),
        catchError((err) => {
          this._finishLoading(true);
          return of(EMPTY);
        }),
      )
      .subscribe((contexts: IContextCompany[]) => {
        // Set the contexts
        this.contextsAvailable = contexts;

        // If an initial context is set, use that when possible
        // TODO: this stinks of component bloat - potentially refactor this to the context service?
        if (this.initialContext?.company && this.initialContext?.property) {
          const initialCompany = contexts.find((loadedCompany: IContextCompany) => {
            return loadedCompany.id === this.initialContext.company;
          });
          if (initialCompany) {
            // Set the initial company, no matter if they have a property or not
            this.form.get("company").setValue(initialCompany.id);

            const initialProperty = initialCompany.properties.find((loadedProperty: IContextProperty) => {
              return loadedProperty.id === this.initialContext.property;
            });
            if (initialProperty) {
              this.form.get("property").setValue(initialProperty.id);
              this.loadNewContext();
              return;
            }
          }
        }

        // If there is only one company and/or property, let's just auto-select it for the user
        if (contexts.length === 1) {
          const context = contexts[0];
          this.form.get("company").setValue(context.id);

          if (contexts[0].properties.length === 1) {
            this.form.get("property").setValue(context.properties[0].id);
            // this.loadNewContext();
          }
        }

        this._updateTitle("Select an Account");
      });
    this._initForm();
  }

  /**
   * Initialize the form
   */
  protected _initForm(): void {
    this.form = this._fb.group({
      company: [
        null,
        [ Validators.required ],
      ],
      property: [
        null,
        [ Validators.required ],
      ],
    });
  }

  public loadNewContext(): void {
    const company = this.contextsAvailable.find((c: IContextCompany) => {
      return c.id === this.form.get("company").value;
    });
    const property = company.properties.find((p: IContextProperty) => {
      return p.id === this.form.get("property").value;
    });

    this._updateContext(company, property);

    this._updateTitle("Loading");

    this._permissionService.load(property.id)
      .pipe(
        filter((loaded) => loaded),
        catchError(() => {
          this._finishLoading(true, true);
          return of(EMPTY);
        })
      )
      .subscribe((results) => {
        this._finishLoading(false, true);

        // TODO: Allow for refreshing of dashboard (either in dashboard routing or adding url param)
        const redirectPath = this._storage.get("redirectUponContext");
        const redirectTo = this._router.parseUrl(redirectPath);
        if (redirectPath && redirectTo) {
          this._storage.remove("redirectUponContext");
          this._router.navigateByUrl(redirectTo);
        } else {
          this._router.navigate(["/dashboard", { p: property.id }]);
        }
      });
  }

  /**
   * Canceling the context selector (only applicable for the context switcher)
   */
  public cancel(): void {
    this.cancelled.emit(true);
  }

  /**
   * Triggers various subjects to notify that loading has finished
   *
   * @param error     If there was an error loading the current item
   * @param complete  If everything has been loaded and the user has a new context
   */
  protected _finishLoading(error: boolean = false, complete: boolean = false): void {
    this.loading$.next(null);
    this.error$.next(error);
    if (complete) {
      this.loading$.complete();
      this.error$.complete();
      this.contextUpdated.emit(true);
    }
  }

  /**
   * Update the various services/components with the new context
   */
  protected _updateContext(company: IContextCompany, property: IContextProperty): void {
    this._contextService.setContext(company, property);
    this.loading$.next("property info");
  }

  protected _updateTitle(title: string): void {
    if (!this.changingContext) {
      this._pageTitle.setTitle(title);
    }
  }
}
